import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Button,
    IconButton,
    Snackbar,
    Tooltip,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { writeRiskOverviewWorkbook } from '../../utils/riskOverviewWorkbook';
import { posSizingActionButtonSx } from '../pos_sizing_table/tableStyles';

const SAFE_EXPORT_ERROR = 'The workbook could not be created. Please try again.';

export default function RiskWorkbookExportButton({
    tooltip,
    ariaLabel,
    fileName,
    sheets,
    disabled = false,
    text = null,
    sx = null,
}) {
    const [isWriting, setIsWriting] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);

    const handleDownload = useCallback(async () => {
        if (disabled || isWriting) return;
        setIsWriting(true);
        setErrorOpen(false);
        try {
            await writeRiskOverviewWorkbook({ fileName, sheets });
        } catch (_) {
            setErrorOpen(true);
        } finally {
            setIsWriting(false);
        }
    }, [disabled, fileName, isWriting, sheets]);

    const buttonDisabled = disabled || isWriting;
    const button = text ? (
        <Button
            variant="outlined"
            size="small"
            onClick={handleDownload}
            disabled={buttonDisabled}
            aria-label={ariaLabel}
            aria-busy={isWriting || undefined}
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            sx={{ ...posSizingActionButtonSx, ...sx }}
        >
            {isWriting ? 'EXPORTING…' : text}
        </Button>
    ) : (
        <IconButton
            size="small"
            onClick={handleDownload}
            disabled={buttonDisabled}
            aria-label={ariaLabel}
            aria-busy={isWriting || undefined}
            sx={{ ...posSizingActionButtonSx, ...sx }}
        >
            <FileDownloadOutlinedIcon fontSize="small" />
        </IconButton>
    );

    return (
        <>
            <Tooltip title={tooltip}>
                <span>{button}</span>
            </Tooltip>
            <Snackbar
                open={errorOpen}
                autoHideDuration={6000}
                onClose={() => setErrorOpen(false)}
            >
                <Alert severity="error" onClose={() => setErrorOpen(false)}>
                    {SAFE_EXPORT_ERROR}
                </Alert>
            </Snackbar>
        </>
    );
}

RiskWorkbookExportButton.propTypes = {
    tooltip: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    sheets: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        kind: PropTypes.oneOf(['json', 'aoa']).isRequired,
        rows: PropTypes.array.isRequired,
    })).isRequired,
    disabled: PropTypes.bool,
    text: PropTypes.string,
    sx: PropTypes.object,
};
